/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

// The timing engine's Evaluation Range (upstream Medaica Viewer v0.9.1, the
// Springer/m1acq engine). A guide payload may carry an `mvp2Timing` block: the
// engine's whole-recording run, the Evaluation Range it selected for results,
// and both runs shaped as viewer markers / metrics / score
// (`viewerFull*` = the whole recording, `viewerEval*` = the range;
// `viewerPeaks/Metrics/Score`, when present, are the whole recording again).
//
// Upstream injects the block into its page globals (`D.peaks`, `D.metrics`,
// `D.score`) and keeps two scopes: results are calculated from the range (the
// metric scope) while the whole recording is drawn for context (the visual
// scope), with the markers outside the range marked `outsideEvalRange` /
// `guideOnly`. Here the two scopes are two payloads derived once per guide:
//
//   metricPayload — the guide payload with the range's peaks / metrics / score;
//                   every derived result (analysisQuality, Timing, Energy,
//                   audit, export package) is computed from it.
//   visualPayload — the metric payload with the whole recording's peaks; the
//                   canvas draws (and hovers) these.
//
// The engine's state for a guide:
//   none        — no `mvp2Timing` (every guide the pipeline produced before the
//                 engine): the guide payload is both scopes, nothing changes.
//   ready       — status "ok" and a selected range: the Evaluation Range page.
//   unavailable — status "error" / "unavailable", or "ok" without a selected
//                 range: upstream's "Analysis unavailable" state. With status
//                 "ok" the engine's markers are still injected (upstream runs
//                 its bootstrap whenever the engine ran); otherwise the guide's
//                 own markers stay.

export const ENGINE_NONE = "none"
export const ENGINE_READY = "ready"
export const ENGINE_UNAVAILABLE = "unavailable"

const finite = (value) => typeof value === "number" && Number.isFinite(value)
const markerList = (value) => (Array.isArray(value) ? value : null)
const metricsObject = (value) => (value && typeof value === "object" ? value : null)

/**
 * The engine block's state for one guide payload: `status` (none / ready /
 * unavailable), the block itself, the selected Evaluation Range (ready only)
 * and whether upstream would inject the engine's markers (status "ok").
 */
export function evaluationRangeEngine(payload) {
  const block = payload?.mvp2Timing
  const mvp2 = block && typeof block === "object" ? block : null
  if (!mvp2) return { status: ENGINE_NONE, mvp2: null, selected: null, injected: false }
  const selected = mvp2.evalRange?.selected || null
  const ok = mvp2.status === "ok"
  const ready = ok && Boolean(selected)
  return {
    status: ready ? ENGINE_READY : ENGINE_UNAVAILABLE,
    mvp2,
    selected: ready ? selected : null,
    injected: ok,
  }
}

/**
 * The selected range's bounds as the canvas uses them (upstream
 * `mvp2EvalGuideRange`): `{start, end}` when both are finite and the range is
 * not empty, else null.
 */
export function evaluationGuideRange(selected) {
  if (!selected) return null
  const start = Number.isFinite(Number(selected.startSec)) ? Number(selected.startSec) : null
  const end = Number.isFinite(Number(selected.endSec)) ? Number(selected.endSec) : null
  return start !== null && end !== null && end > start ? { start, end } : null
}

/** Upstream `mvp2OutsideEvalGuideContext`: whether time `t` is drawn as context outside the range. */
export function outsideEvaluationRange(range, t) {
  return Boolean(range && !(Number(t) >= range.start && Number(t) <= range.end))
}

/**
 * The metric and visual payloads for a guide (upstream `_inject_mvp2_initial_timing`
 * bootstrap followed by `__mvp2ApplyAnalysisScope('eval_visual_full')`).
 */
export function evaluationRangeScopes(payload) {
  const engine = evaluationRangeEngine(payload)
  if (!engine.injected) return { engine, metricPayload: payload, visualPayload: payload }
  const mvp2 = engine.mvp2
  // The whole-recording scope; `viewerPeaks/Metrics/Score` default to it.
  const fullPeaks = markerList(mvp2.viewerFullPeaks) || markerList(mvp2.viewerPeaks) || []
  const fullMetrics = metricsObject(mvp2.viewerFullMetrics) || metricsObject(mvp2.viewerMetrics) || {}
  const viewerPeaks = markerList(mvp2.viewerPeaks) || fullPeaks
  const viewerMetrics = metricsObject(mvp2.viewerMetrics) || fullMetrics
  const viewerScore = finite(mvp2.viewerScore) ? mvp2.viewerScore : mvp2.viewerFullScore
  const scorePerFrame = mvp2.confidence?.scorePerFrame
  let bootstrapScore = payload.score
  if (finite(viewerScore)) bootstrapScore = viewerScore
  else if (finite(scorePerFrame)) bootstrapScore = scorePerFrame
  // The Evaluation Range scope (the whole recording when no range was selected).
  const evalPeaks = markerList(mvp2.viewerEvalPeaks) || viewerPeaks
  const evalMetrics = metricsObject(mvp2.viewerEvalMetrics) || viewerMetrics
  const evalScore = finite(mvp2.viewerEvalScore) ? mvp2.viewerEvalScore : bootstrapScore

  const bootstrapPayload = { ...payload, peaks: viewerPeaks, metrics: viewerMetrics, score: bootstrapScore }
  // Upstream applies the two scopes only when the whole recording has markers.
  if (!fullPeaks.length) return { engine, metricPayload: bootstrapPayload, visualPayload: bootstrapPayload }
  const metricPayload = {
    ...payload,
    peaks: evalPeaks,
    metrics: { ...evalMetrics },
    score: finite(Number(evalScore)) ? Number(evalScore) : bootstrapScore,
  }
  const visualPayload = { ...metricPayload, peaks: fullPeaks }
  return { engine, metricPayload, visualPayload }
}

/**
 * The view framed on the range, once per recording (upstream
 * `__mvp2FrameEvalRange`): the range fills three quarters of the visible
 * window (at least 1.5 s, at most the recording), centred. Returns the
 * `{start, width}` to apply before clamping, or null without a range.
 */
export function evaluationRangeFrame(selected, duration) {
  if (!selected || !duration) return null
  const start = Number(selected.startSec)
  const end = Number(selected.endSec)
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null
  const rangeDuration = Math.max(0.1, end - start)
  const width = Math.min(Number(duration) || rangeDuration, Math.max(rangeDuration / 0.75, 1.5))
  const center = (start + end) / 2
  return { start: center - width / 2, width }
}

/**
 * Where playback starts, once per recording (upstream `__mvp2SeekEvalStartOnce`):
 * the range's start, within the recording; null without a range.
 */
export function evaluationRangeStart(selected, duration) {
  if (!selected) return null
  const start = Number(selected.startSec)
  if (!Number.isFinite(start)) return null
  return Math.max(0, Math.min(Number(duration) || start, start))
}

/**
 * The Confidence row (upstream `#mvp2ConfidenceRow`, filled by
 * `renderEvalScope`): Signal from the range's per-frame score, Timing and
 * Variation from the engine's S1-to-S1 interval CV over the range (its
 * `rrCv`; above 0.08 grades Moderate), Energy from the metric scope's Energy
 * gate and the range's cycle count. A missing input takes the optimistic
 * branch, as upstream's does. The S1-to-S1 interval CV is a stability /
 * confidence input only: the reported timing variability is the viewer's
 * central spread (timingConsistency).
 */
export function evaluationRangeConfidence(selected, quality) {
  const timing = selected?.timing || {}
  const confidence = selected?.confidence || {}
  const cycles = Number(timing.cycleCount)
  const s1IntervalCv = Number(timing.rrCv)
  const score = Number(confidence.scorePerFrame)
  const energyReady = Number.isFinite(cycles) && cycles >= 8
  const rhythm = Number.isFinite(s1IntervalCv) && s1IntervalCv > 0.08 ? "Moderate" : "High"
  let energy = "Limited"
  if (energyReady) energy = quality?.visualEnergyAllowed ? "High" : "Moderate"
  return {
    signal: Number.isFinite(score) && score < 0.12 ? "Moderate" : "High",
    timing: rhythm,
    variation: rhythm,
    energy,
  }
}

/** The strip's line (upstream `updateAnalysisStrip`): "Evaluation range for results · a–b s · N cycles". */
export function evaluationRangeSummary(selected) {
  const timing = selected?.timing || {}
  const cycles = Number(timing.cycleCount)
  return `Evaluation range for results · ${Number(selected.startSec).toFixed(1)}–${Number(selected.endSec).toFixed(
    1
  )}s · ${Number.isFinite(cycles) ? cycles : "—"} cycles`
}
